import {
  IInheritedSetting,
  InheritedSettingNameType,
  IMinimumOrderValueSetting,
  IMinimumOrderQuantitySetting,
  OrderedInheritedSettings
} from "src/core/drops/api/drops.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  PaginatedParams,
  FilterParams
} from "src/core/shared/api";
import { PaginatedData } from "src/library/utils/apiTypes";

import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "./config.api";

export type DeleteSettingParams = { id: number};
export type UpdateSettingParams = IInheritedSetting;
export type CreateSettingParams<T extends InheritedSettingNameType = InheritedSettingNameType> = Pick<
  IInheritedSetting<T>,
  "entityId" | "scope" | "value" | "name"
>;
// currently only filtering by name is required
export type InheritedSettingsScopeFilterParams = FilterParams<{ name?: string }>;
export type GetSettingParams = { name: string };
export type GetSettingsParams = { name?: string[] };
export type GetSettingsByScopeParams = {
  scope: string;
  pagination: PaginatedParams<IInheritedSetting>;
  filters: InheritedSettingsScopeFilterParams;
};

export type GetCustomerSettingParams = GetSettingParams & { customerId: number };
export type GetDropSettingParams = GetSettingParams & { dropId: number };

// transform to number were required
const sanitiseSettingData = (data: UpdateSettingParams | CreateSettingParams) => {
  switch (data.name) {
    case "minimum_order_value": {
      const dataValue = data.value as IMinimumOrderValueSetting;

      if (typeof dataValue.value === "string") {
        (data.value as IMinimumOrderValueSetting).value = parseInt(dataValue.value, 10);
      }

      return data;
    }
    case "minimum_order_quantity": {
      const dataValue = data.value as IMinimumOrderQuantitySetting;

      if (typeof dataValue.quantity === "string") {
        (data.value as IMinimumOrderQuantitySetting).quantity = parseInt(dataValue.quantity, 10);
      }

      return data;
    }
    default: {
      return data;
    }
  }
};

// for "any" below, reference keyof InheritedSettingValue
type InheritedSettingEndpointConfig = {
  GET_SETTINGS_BY_SCOPE: EndpointConfig<
    GetSettingsByScopeParams,
    PaginatedData<IInheritedSetting>
  >;
  GET_CUSTOMER_SETTINGS: EndpointConfig<
    { customerId: number },
    OrderedInheritedSettings<IInheritedSetting[]>
  >;
  GET_CUSTOMER_SETTING: EndpointConfig<
    GetCustomerSettingParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Array<IInheritedSetting<any>>
  >;
  GET_BRANCH_SETTINGS: EndpointConfig<
    { dropId: number },
    OrderedInheritedSettings<IInheritedSetting[]>
  >;
  GET_BRANCH_SETTING: EndpointConfig<
    GetDropSettingParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Array<IInheritedSetting<any>>
    >;
    GET_GLOBAL_SETTINGS: EndpointConfig<
    GetSettingsParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Array<IInheritedSetting<any>>
  >;
  GET_GLOBAL_SETTING: EndpointConfig<
    GetSettingParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    IInheritedSetting<any>
  >;
  CREATE_SETTING: EndpointConfig<
    CreateSettingParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    IInheritedSetting<any>
  >;
  UPDATE_SETTING: EndpointConfig<
    UpdateSettingParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    IInheritedSetting<any>
  >;
  DELETE_SETTING: EndpointConfig<
    DeleteSettingParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    IInheritedSetting<any>
  >;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/settings`;

/**
 * Inherited Setting API
 */
const requests: RequestConfig<InheritedSettingEndpointConfig> = {
  GET_SETTINGS_BY_SCOPE: params => ({
    url: `${BASE_URL}/scope/${params.scope}`,
    method: "GET",
    params
  }),
  GET_CUSTOMER_SETTINGS: data => ({
    url: `${BASE_URL}/customer/${data.customerId}`,
    method: "GET"
  }),
  GET_CUSTOMER_SETTING: params => ({
    url: `${BASE_URL}/customer/${params.customerId}/name/${params.name}`,
    method: "GET"
  }),
  GET_BRANCH_SETTINGS: params => ({
    url: `${BASE_URL}/branch/${params.dropId}`,
    method: "GET"
  }),
  GET_BRANCH_SETTING: params => ({
    url: `${BASE_URL}/branch/${params.dropId}/name/${params.name}`,
    method: "GET"
  }),
  GET_GLOBAL_SETTINGS: params => ({
    url: `${BASE_URL}/global`,
    method: "GET",
    params: params && params.name ? { name: params.name.join(",") } : undefined
  }),
  GET_GLOBAL_SETTING: params => ({
    url: `${BASE_URL}/global/${params.name}`,
    method: "GET",
    params
  }),
  CREATE_SETTING: data => ({
    url: `${BASE_URL}`,
    method: "POST",
    data: sanitiseSettingData(data)
  }),
  UPDATE_SETTING: data => ({
    url: `${BASE_URL}`,
    method: "PUT",
    data: sanitiseSettingData(data)
  }),
  DELETE_SETTING: data => ({
    url: `${BASE_URL}/${data.id}`,
    method: "DELETE"
  })
};

export const useInheritedSettingsApi: UseApiRequest<InheritedSettingEndpointConfig> = name => useAxios(requests[ name ], name);
